import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, memo } from 'react';
import './LogoLoop.css';

const ANIMATION_CONFIG = { SMOOTH_TAU: 0.25, MIN_COPIES: 2, COPY_HEADROOM: 2 };

const toCssLength = value => (typeof value === 'number' ? `${value}px` : (value ?? undefined));

const useAnimationLoop = (trackRef, targetVelocity, seqSizeRef, isHovered, hoverSpeed, isVertical) => {
  const rafRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const offsetRef = useRef(0);
  const velocityRef = useRef(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const animate = timestamp => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp;
      }

      const deltaTime = Math.max(0, timestamp - lastTimestampRef.current) / 1000;
      lastTimestampRef.current = timestamp;

      const target = isHovered && hoverSpeed !== undefined ? hoverSpeed : targetVelocity;
      const easingFactor = 1 - Math.exp(-deltaTime / ANIMATION_CONFIG.SMOOTH_TAU);
      velocityRef.current += (target - velocityRef.current) * easingFactor;

      const seqSize = seqSizeRef.current;
      if (seqSize > 0) {
        let nextOffset = offsetRef.current + velocityRef.current * deltaTime;
        nextOffset = ((nextOffset % seqSize) + seqSize) % seqSize;
        offsetRef.current = nextOffset;

        track.style.transform = isVertical
          ? `translate3d(0, ${-offsetRef.current}px, 0)`
          : `translate3d(${-offsetRef.current}px, 0, 0)`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTimestampRef.current = null;
    };
  }, [targetVelocity, isHovered, hoverSpeed, isVertical, trackRef, seqSizeRef]);
};

export const LogoLoop = memo(
  ({
    logos,
    speed = 120,
    direction = 'left',
    width = '100%',
    logoHeight = 28,
    gap = 32,
    pauseOnHover,
    hoverSpeed,
    fadeOut = false,
    fadeOutColor,
    scaleOnHover = false,
    renderItem,
    ariaLabel = 'Partner logos',
    className,
    style
  }) => {
    const containerRef = useRef(null);
    const trackRef = useRef(null);
    const seqRef = useRef(null);
    const seqSizeRef = useRef(0);
    const copyCountRef = useRef(ANIMATION_CONFIG.MIN_COPIES);
    const measureRafRef = useRef(null);
    const pendingRemeasureRef = useRef(false);

    const [copyCount, setCopyCount] = useState(ANIMATION_CONFIG.MIN_COPIES);
    const [isHovered, setIsHovered] = useState(false);

    const effectiveHoverSpeed = useMemo(() => {
      if (hoverSpeed !== undefined) return hoverSpeed;
      if (pauseOnHover === true) return 0;
      if (pauseOnHover === false) return undefined;
      return 0;
    }, [hoverSpeed, pauseOnHover]);

    const isVertical = direction === 'up' || direction === 'down';

    const targetVelocity = useMemo(() => {
      const magnitude = Math.abs(speed);
      let directionMultiplier;
      if (isVertical) {
        directionMultiplier = direction === 'up' ? 1 : -1;
      } else {
        directionMultiplier = direction === 'left' ? 1 : -1;
      }
      const speedMultiplier = speed < 0 ? -1 : 1;
      return magnitude * directionMultiplier * speedMultiplier;
    }, [speed, direction, isVertical]);

    const logoKey = useMemo(
      () => logos.map(item => item.src ?? item.title ?? '').join('\0'),
      [logos]
    );

    const measureDimensions = useCallback(() => {
      if (measureRafRef.current !== null) {
        cancelAnimationFrame(measureRafRef.current);
      }

      measureRafRef.current = requestAnimationFrame(() => {
        measureRafRef.current = null;

        const containerEl = containerRef.current;
        const seqEl = seqRef.current;
        if (!containerEl || !seqEl) return;

        if (isVertical) {
          const parentHeight = containerEl.parentElement?.clientHeight ?? 0;
          if (parentHeight > 0) {
            const targetHeight = Math.ceil(parentHeight);
            if (containerEl.style.height !== `${targetHeight}px`) {
              containerEl.style.height = `${targetHeight}px`;
            }
          }

          const sequenceHeight = seqEl.scrollHeight;
          if (sequenceHeight <= 0) return;

          const nextSize = Math.ceil(sequenceHeight);
          const viewport = containerEl.clientHeight || parentHeight || sequenceHeight;
          const nextCopyCount = Math.max(
            ANIMATION_CONFIG.MIN_COPIES,
            Math.ceil(viewport / sequenceHeight) + ANIMATION_CONFIG.COPY_HEADROOM
          );

          seqSizeRef.current = nextSize;

          if (copyCountRef.current !== nextCopyCount) {
            copyCountRef.current = nextCopyCount;
            pendingRemeasureRef.current = true;
            setCopyCount(nextCopyCount);
          }
          return;
        }

        const containerWidth = containerEl.clientWidth;
        const sequenceWidth = seqEl.scrollWidth;
        if (sequenceWidth <= 0) return;

        const nextSize = Math.ceil(sequenceWidth);
        const nextCopyCount = Math.max(
          ANIMATION_CONFIG.MIN_COPIES,
          Math.ceil(containerWidth / sequenceWidth) + ANIMATION_CONFIG.COPY_HEADROOM
        );

        seqSizeRef.current = nextSize;

        if (copyCountRef.current !== nextCopyCount) {
          copyCountRef.current = nextCopyCount;
          pendingRemeasureRef.current = true;
          setCopyCount(nextCopyCount);
        }
      });
    }, [isVertical]);

    useLayoutEffect(() => {
      if (!pendingRemeasureRef.current) return;
      pendingRemeasureRef.current = false;
      measureDimensions();
    }, [copyCount, measureDimensions]);

    useEffect(() => {
      measureDimensions();

      const containerEl = containerRef.current;
      if (!containerEl) return;

      const handleResize = () => measureDimensions();
      window.addEventListener('resize', handleResize);

      let resizeObserver;
      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(containerEl);
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver?.disconnect();
        if (measureRafRef.current !== null) {
          cancelAnimationFrame(measureRafRef.current);
          measureRafRef.current = null;
        }
      };
    }, [measureDimensions, logoKey, gap, logoHeight]);

    useEffect(() => {
      const seqEl = seqRef.current;
      if (!seqEl) return;

      const images = seqEl.querySelectorAll('img');
      if (images.length === 0) return;

      let pending = 0;
      const handleImageLoad = () => {
        pending -= 1;
        if (pending === 0) measureDimensions();
      };

      images.forEach(img => {
        if (img.complete) return;
        pending += 1;
        img.addEventListener('load', handleImageLoad, { once: true });
        img.addEventListener('error', handleImageLoad, { once: true });
      });

      if (pending === 0) measureDimensions();

      return () => {
        images.forEach(img => {
          img.removeEventListener('load', handleImageLoad);
          img.removeEventListener('error', handleImageLoad);
        });
      };
    }, [measureDimensions, logoKey]);

    useAnimationLoop(trackRef, targetVelocity, seqSizeRef, isHovered, effectiveHoverSpeed, isVertical);

    const cssVariables = useMemo(
      () => ({
        '--logoloop-gap': `${gap}px`,
        '--logoloop-logoHeight': `${logoHeight}px`,
        ...(fadeOutColor && { '--logoloop-fadeColor': fadeOutColor })
      }),
      [gap, logoHeight, fadeOutColor]
    );

    const isTransparentFade = fadeOutColor === 'transparent';

    const rootClassName = useMemo(
      () =>
        [
          'logoloop',
          isVertical ? 'logoloop--vertical' : 'logoloop--horizontal',
          fadeOut && 'logoloop--fade',
          fadeOut && isTransparentFade && 'logoloop--fade-transparent',
          scaleOnHover && 'logoloop--scale-hover',
          className
        ]
          .filter(Boolean)
          .join(' '),
      [isVertical, fadeOut, isTransparentFade, scaleOnHover, className]
    );

    const handleMouseEnter = useCallback(() => {
      if (effectiveHoverSpeed !== undefined) setIsHovered(true);
    }, [effectiveHoverSpeed]);

    const handleMouseLeave = useCallback(() => {
      if (effectiveHoverSpeed !== undefined) setIsHovered(false);
    }, [effectiveHoverSpeed]);

    const renderLogoItem = useCallback(
      (item, key) => {
        if (renderItem) {
          return (
            <li className="logoloop__item" key={key}>
              {renderItem(item, key)}
            </li>
          );
        }

        const isNodeItem = 'node' in item;
        const content = isNodeItem ? (
          <span className="logoloop__node" aria-hidden={!!item.href && !item.ariaLabel}>
            {item.node}
          </span>
        ) : (
          <img
            src={item.src}
            srcSet={item.srcSet}
            sizes={item.sizes}
            width={item.width}
            height={item.height}
            alt={item.alt ?? ''}
            title={item.title}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        );

        const itemAriaLabel = isNodeItem ? (item.ariaLabel ?? item.title) : (item.alt ?? item.title);
        const itemContent = item.href ? (
          <a
            className="logoloop__link"
            href={item.href}
            aria-label={itemAriaLabel || 'logo link'}
            target="_blank"
            rel="noreferrer noopener"
          >
            {content}
          </a>
        ) : (
          content
        );

        return (
          <li className="logoloop__item" key={key}>
            {itemContent}
          </li>
        );
      },
      [renderItem]
    );

    const logoLists = useMemo(
      () =>
        Array.from({ length: copyCount }, (_, copyIndex) => (
          <ul
            className="logoloop__list"
            key={`copy-${copyIndex}`}
            aria-hidden={copyIndex > 0}
            ref={copyIndex === 0 ? seqRef : undefined}
          >
            {logos.map((item, itemIndex) => renderLogoItem(item, `${copyIndex}-${itemIndex}`))}
          </ul>
        )),
      [copyCount, logos, renderLogoItem]
    );

    const containerStyle = useMemo(
      () => ({
        width: isVertical
          ? toCssLength(width) === '100%'
            ? undefined
            : toCssLength(width)
          : (toCssLength(width) ?? '100%'),
        ...cssVariables,
        ...style
      }),
      [width, cssVariables, style, isVertical]
    );

    return (
      <div ref={containerRef} className={rootClassName} style={containerStyle} role="region" aria-label={ariaLabel}>
        <div className="logoloop__track" ref={trackRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {logoLists}
        </div>
      </div>
    );
  }
);

LogoLoop.displayName = 'LogoLoop';

export default LogoLoop;
